import DOMPurify from "dompurify";
import { useMemo, type HTMLAttributes, type Ref } from "react";

/**
 * ═══ LE SEUL ENDROIT OÙ DU HTML LIBRE ENTRE DANS LA PAGE ═══
 *
 * Le HTML des organisateurs (sections, textes d'accueil, décors de formulaire, écrans
 * du direct) était posé par dix-sept `dangerouslySetInnerHTML` dispersés, sans aucun
 * assainissement. Or le serveur laisse encore écrire ces réglages à tout inscrit du
 * domaine tant que `RIGHTS_GUARD_MODE=enforce` n'est pas posé (`api/src/hooks/
 * useRightsGuard.js`) : un `<img onerror>` déposé une fois s'exécutait chez tous les
 * participants, jeton en `localStorage` compris.
 *
 * ⚠️ ASSAINIR, PAS APPAUVRIR. Ce HTML est un contrat vendu au client (voir README,
 * « trappe de CSS libre ») : on retire ce qui EXÉCUTE (scripts, gestionnaires `on*`,
 * `javascript:`), on garde tout le reste — `style`, `class`, `id`, `target`, les
 * `iframe` de vidéo (YouTube, Vimeo, Dailymotion) que les sections embarquent déjà.
 *
 * ⚠️ MÉMOÏSÉ : `{ __html }` recréé à chaque rendu repose l'`innerHTML` à CHAQUE commit
 * (mesuré sur la section, voir la mémoire du projet), et les écrans du direct se
 * rendent à chaque message WebSocket.
 */

const ALLOWED_URI = /^(?:(?:https?|mailto|tel|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

const EMBED_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "player.vimeo.com",
  "www.dailymotion.com",
  "geo.dailymotion.com",
];

let hooked = false;
function ensureHooks(): void {
  if (hooked) return;
  hooked = true;
  // Une `iframe` ne survit que vers un hébergeur de vidéo connu : c'est la seule
  // raison d'en accepter, et une `iframe` vers n'importe où est une page dans la page.
  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName !== "iframe") return;
    const src = (node as Element).getAttribute("src") ?? "";
    let host = "";
    try {
      host = new URL(src, "https://x.invalid").hostname;
    } catch {
      host = "";
    }
    if (!EMBED_HOSTS.includes(host)) node.parentNode?.removeChild(node);
  });
}

export function sanitize(html: string): string {
  ensureHooks();
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true, svg: true, svgFilters: true },
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "target",
      "allow",
      "allowfullscreen",
      "frameborder",
      "loading",
      "referrerpolicy",
    ],
    ALLOWED_URI_REGEXP: ALLOWED_URI,
    // `target="_blank"` reste autorisé ; DOMPurify ne pose pas `rel`, on le fait ici.
    ADD_URI_SAFE_ATTR: [],
  }).replace(
    /<a\b([^>]*\btarget=["']?_blank["']?[^>]*)>/gi,
    (m, attrs: string) =>
      /\brel=/i.test(attrs) ? m : `<a${attrs} rel="noopener noreferrer">`,
  );
}

type Props = {
  html: string;
  className?: string;
  ref?: Ref<HTMLDivElement>;
  /** Balise porteuse : `div` par défaut, `section`/`p` quand la maquette l'exige. */
  as?: "div" | "section" | "p" | "span";
} & Omit<HTMLAttributes<HTMLElement>, "children" | "dangerouslySetInnerHTML">;

export function Prose({ html, as = "div", ref, ...rest }: Props) {
  const markup = useMemo(() => ({ __html: sanitize(html) }), [html]);
  const Tag = as;
  return (
    <Tag
      ref={ref as Ref<HTMLDivElement> & Ref<HTMLParagraphElement>}
      {...rest}
      dangerouslySetInnerHTML={markup}
    />
  );
}
